/** Pure authority rules for a fixture held closed across list generations. */

const normalizedBoundaryValue = value => String(value ?? '').trim().toLowerCase();

const normalizedRevision = value => {
  if (value == null || String(value).trim() === '') return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
};

const responseIds = values => new Set(
  (values instanceof Set ? [...values] : Array.isArray(values) ? values : [])
    .filter(value => value != null)
    .map(String),
);

const boundaryMatches = (left = {}, right = {}) => (
  left.requestKey === right.requestKey
  && normalizedBoundaryValue(left.mode) === normalizedBoundaryValue(right.mode)
  && normalizedBoundaryValue(left.scope) === normalizedBoundaryValue(right.scope)
);

export const createSportsnewMembershipQuarantine = ({
  requestKey,
  baselineRevision,
  mode,
  scope,
} = {}) => Object.freeze({
  requestKey,
  baselineRevision: normalizedRevision(baselineRevision),
  mode: normalizedBoundaryValue(mode),
  scope: normalizedBoundaryValue(scope),
  reopen: null,
});

export const armSportsnewMembershipQuarantineReopen = (quarantine, {
  requestKey,
  baselineRevision,
  mode,
  scope,
} = {}) => {
  if (!quarantine || requestKey == null) return quarantine;
  const currentBaseline = normalizedRevision(baselineRevision);
  return Object.freeze({
    ...quarantine,
    reopen: Object.freeze({
      requestKey,
      baselineRevision: currentBaseline ?? normalizedRevision(quarantine.baselineRevision),
      mode: normalizedBoundaryValue(mode),
      scope: normalizedBoundaryValue(scope),
    }),
  });
};

export const sportsnewMembershipQuarantineTargetsBoundary = (quarantine, boundary = {}) => {
  if (!quarantine) return false;
  return boundaryMatches(quarantine.reopen ?? quarantine, boundary);
};

export const shouldReleaseSportsnewMembershipQuarantine = ({
  quarantine,
  fixtureId,
  requestKey,
  responseFixtureIds,
  mode,
  scope,
} = {}) => {
  if (!quarantine || fixtureId == null) return false;
  const boundary = { requestKey, mode, scope };
  const ids = responseIds(responseFixtureIds);

  // Once a real addition/resume was observed, an absence from another filter
  // is not reopening evidence. Only the armed post-event boundary may release,
  // and its authoritative row must contain the fixture.
  if (quarantine.reopen) {
    if (!boundaryMatches(quarantine.reopen, boundary) || !ids.has(String(fixtureId))) {
      return false;
    }
    // The caller accepts only a cache-busted REST request whose sequence was
    // allocated after the explicit reopen event.  Fixture membership rows use
    // per-row revisions, so the aggregate MAX token can legitimately remain
    // unchanged when a different, lower-revision row is added again.  Presence
    // in that post-event boundary is the authority; requiring MAX to advance
    // would leave the fixture and its counts quarantined forever.
    return true;
  }

  if (!boundaryMatches(quarantine, boundary)) return false;
  // The same post-event request-sequence fence applies to terminal/removal
  // confirmation.  Absence is required even if an unrelated row advanced the
  // aggregate revision; conversely, an unchanged MAX token must not keep an
  // authoritatively absent fixture in a permanent fail-closed refresh loop.
  return !ids.has(String(fixtureId));
};

export default Object.freeze({
  armSportsnewMembershipQuarantineReopen,
  createSportsnewMembershipQuarantine,
  shouldReleaseSportsnewMembershipQuarantine,
  sportsnewMembershipQuarantineTargetsBoundary,
});
